import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import UpgradeModal from '../upgrade/UpgradeModal';
import { useAuth } from '../../contexts/AuthContext';

const FEATURES = [
  { label: 'Live Odds Board',                       free: true,       pro: true,        premium: true },
  { label: 'Line Movement Charts',                   free: true,       pro: true,        premium: true },
  { label: 'AI Predictions (teaser)',                free: true,       pro: true,        premium: true },
  { label: 'AI Predictions (full)',                  free: false,      pro: true,        premium: true },
  { label: 'Daily Briefing (teaser)',                free: true,       pro: true,        premium: true },
  { label: 'Daily Briefing (full)',                  free: false,      pro: true,        premium: true },
  { label: 'Game Chat',                              free: '2/day',    pro: '10/day',    premium: 'Unlimited' },
  { label: 'Betting Education Q&A',                  free: '2/day',    pro: 'Unlimited', premium: 'Unlimited' },
  { label: 'AI Prop Picks',                          free: false,      pro: true,        premium: true },
  { label: 'Bet Tracker',                            free: false,      pro: true,        premium: true },
  { label: 'Game Preview & Line Analysis',           free: false,      pro: true,        premium: true },
  { label: 'Parlay Analysis',                        free: false,      pro: true,        premium: true },
  { label: 'Pattern Finder — Team Analysis',         free: false,      pro: true,        premium: true },
  { label: 'AI Betting Review',                      free: false,      pro: false,       premium: true },
  { label: 'Pattern Finder — Matchup Analysis',      free: false,      pro: false,       premium: true },
  { label: 'Pattern Finder — Bookmaker Analysis',    free: false,      pro: false,       premium: true },
  { label: 'Alert History',                          free: '6 hours',  pro: 'Full',      premium: 'Full' },
];

function FeatureValue({ value }) {
  if (value === true) {
    return (
      <svg className="w-4 h-4 text-[#029456] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  if (value === false) {
    return <span className="text-[#9CA3AF] dark:text-[#505a6e] text-[13px] leading-none">—</span>;
  }
  return (
    <span
      className="text-[11px] font-medium text-[#4B5563] dark:text-[#94a3b8] whitespace-nowrap"
      style={{ fontFamily: 'var(--font-mono)' }}
    >
      {value}
    </span>
  );
}

function CtaButton({ cta }) {
  if (cta === null) {
    return <div className="h-[34px]" />;
  }
  if (cta.action === 'disabled') {
    return (
      <button
        disabled
        className="w-full rounded-md px-4 py-2 text-[13px] font-medium border border-[#E5E7EB] dark:border-[#1e2536] text-[#9CA3AF] dark:text-[#505a6e] cursor-default"
        style={{ fontFamily: 'var(--font-sans)' }}
      >
        {cta.label}
      </button>
    );
  }
  if (cta.action === 'link') {
    return (
      <Link
        to={cta.to}
        className="block w-full text-center rounded-md px-4 py-2 text-[13px] font-medium bg-[#005BB3] hover:bg-[#004a93] text-white transition-colors duration-150"
        style={{ fontFamily: 'var(--font-sans)' }}
      >
        {cta.label}
      </Link>
    );
  }
  return (
    <button
      onClick={cta.onClick}
      className="w-full rounded-md px-4 py-2 text-[13px] font-medium bg-[#005BB3] hover:bg-[#004a93] text-white transition-colors duration-150"
      style={{ fontFamily: 'var(--font-sans)' }}
    >
      {cta.label}
    </button>
  );
}

export default function PricingPage() {
  const [billing, setBilling] = useState('monthly');
  const [modal, setModal] = useState({ open: false, tier: 'pro' });
  const { user } = useAuth();
  const effectiveTier = useEffectiveTier();

  function openModal(tier) {
    setModal({ open: true, tier });
  }

  function getCta(tierKey) {
    if (tierKey === 'free') {
      if (!user) return { label: 'Get Started', action: 'link', to: '/auth' };
      if (effectiveTier === 'free') return { label: 'Current Plan', action: 'disabled' };
      return null;
    }
    if (tierKey === 'pro') {
      if (effectiveTier === 'pro') return { label: 'Current Plan', action: 'disabled' };
      if (effectiveTier === 'premium') return null;
      return {
        label: !user ? 'Get Started' : 'Upgrade to Pro',
        action: 'modal',
        onClick: () => openModal('pro'),
      };
    }
    if (tierKey === 'premium') {
      if (effectiveTier === 'premium') return { label: 'Current Plan', action: 'disabled' };
      return {
        label: !user ? 'Get Started' : 'Upgrade to Premium',
        action: 'modal',
        onClick: () => openModal('premium'),
      };
    }
    return null;
  }

  const tiers = [
    {
      key: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      savings: null,
      popular: false,
    },
    {
      key: 'pro',
      name: 'Pro',
      price: billing === 'monthly' ? '$19.99' : '$199',
      period: billing === 'monthly' ? '/mo' : '/yr',
      savings: billing === 'annual' ? 'Save ~$40 vs monthly' : null,
      popular: true,
    },
    {
      key: 'premium',
      name: 'Premium',
      price: billing === 'monthly' ? '$39.99' : '$399',
      period: billing === 'monthly' ? '/mo' : '/yr',
      savings: billing === 'annual' ? 'Save ~$80 vs monthly' : null,
      popular: false,
    },
  ];

  return (
    <div className="px-5 py-6">
      <div style={{ maxWidth: 1100 }}>
        {/* Header */}
        <div className="mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#005BB3] dark:text-[#3b82f6] mb-1">
            Subscription
          </p>
          <h1
            className="text-[22px] font-bold text-[#111827] dark:text-[#e2e8f0] mb-1"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            Plans &amp; Pricing
          </h1>
          <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5">
            Choose the plan that fits your game
          </p>

          {/* Billing toggle */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-[#F8F9FA] dark:bg-[#0a0d14] p-0.5">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-4 py-1.5 text-[13px] font-medium rounded transition-colors duration-150 ${
                  billing === 'monthly'
                    ? 'bg-white dark:bg-[#151a25] text-[#005BB3] dark:text-[#3b82f6] shadow-sm'
                    : 'text-[#6B7280] dark:text-[#8892a8] hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
                }`}
                style={{ fontFamily: 'var(--font-sans)' }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`px-4 py-1.5 text-[13px] font-medium rounded transition-colors duration-150 ${
                  billing === 'annual'
                    ? 'bg-white dark:bg-[#151a25] text-[#005BB3] dark:text-[#3b82f6] shadow-sm'
                    : 'text-[#6B7280] dark:text-[#8892a8] hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
                }`}
                style={{ fontFamily: 'var(--font-sans)' }}
              >
                Annual
              </button>
            </div>
            <span className={`text-[12px] font-medium transition-colors duration-150 ${billing === 'annual' ? 'text-[#029456]' : 'text-[#9CA3AF] dark:text-[#505a6e]'}`}>
              Save 2 months with annual billing
            </span>
          </div>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          {tiers.map((tier) => {
            const isCurrentTier = effectiveTier === tier.key;
            const cta = getCta(tier.key);

            return (
              <div
                key={tier.key}
                className={`flex flex-col rounded-lg border transition-colors ${
                  isCurrentTier
                    ? 'border-[#005BB3] dark:border-[#3b82f6]'
                    : 'border-[#E5E7EB] dark:border-[#1e2536]'
                } bg-white dark:bg-[#111827]`}
              >
                {/* Card header */}
                <div className="px-5 pt-5 pb-4 border-b border-[#E5E7EB] dark:border-[#1e2536]">
                  {/* Name + badges */}
                  <div className="flex items-center gap-2 flex-wrap mb-3 min-h-[22px]">
                    <span
                      className="text-[15px] font-semibold text-[#111827] dark:text-[#e2e8f0]"
                      style={{ fontFamily: 'var(--font-sans)' }}
                    >
                      {tier.name}
                    </span>
                    {tier.popular && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-[#F86A38]/15 text-[#F86A38]">
                        Most Popular
                      </span>
                    )}
                    {isCurrentTier && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-[#005BB3]/10 text-[#005BB3] dark:text-[#3b82f6]">
                        Your Plan
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-1">
                    <span
                      className="text-[28px] font-bold text-[#111827] dark:text-[#e2e8f0] leading-none"
                      style={{ fontFamily: 'var(--font-mono)' }}
                    >
                      {tier.price}
                    </span>
                    <span className="text-[13px] text-[#6B7280] dark:text-[#8892a8]">
                      {tier.period}
                    </span>
                  </div>

                  {/* Savings note (annual) or spacer */}
                  <div className="mb-3 min-h-[16px]">
                    {tier.savings && (
                      <p className="text-[11px] text-[#029456] font-medium">{tier.savings}</p>
                    )}
                  </div>

                  {/* CTA */}
                  <CtaButton cta={cta} />
                </div>

                {/* Feature list */}
                <div className="px-5 py-4 flex-1">
                  <ul className="space-y-2.5">
                    {FEATURES.map((feature) => (
                      <li key={feature.label} className="flex items-start justify-between gap-3">
                        <span
                          className="text-[12px] text-[#4B5563] dark:text-[#8892a8] leading-snug"
                          style={{ fontFamily: 'var(--font-sans)' }}
                        >
                          {feature.label}
                        </span>
                        <span className="flex items-center flex-shrink-0 mt-0.5">
                          <FeatureValue value={feature[tier.key]} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <UpgradeModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        suggestedTier={modal.tier}
        triggerFeature="pricing-page"
      />
    </div>
  );
}
